import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    ArrowUpRight,
    CalendarDays,
    ChevronRight,
    FileText,
    Hash,
    ListFilter,
    LockKeyhole,
    MessageCircle,
    NotebookTabs,
    Orbit,
    Search,
    ShieldCheck,
    Sparkles,
    Tag,
    UserRound,
    UsersRound,
    X,
} from "lucide-react";
import FooterNav from "../components/FooterNav";
import {
    fetchGlobalSearch,
    type GlobalSearchDate,
    type GlobalSearchItem,
    type GlobalSearchNoteScope,
    type GlobalSearchSort,
    type GlobalSearchSpaceAccess,
    type GlobalSearchType,
} from "../api/search";
import { formatBlackspaceDateTime } from "../utils/time";

const SEARCH_LIMIT = 18;

const emptyTotals = {
    all: 0,
    posts: 0,
    people: 0,
    spaces: 0,
    notes: 0,
    tags: 0,
};

const dateFilters: Array<{ id: GlobalSearchDate; label: string }> = [
    { id: "all", label: "Any time" },
    { id: "24h", label: "24h" },
    { id: "7d", label: "7d" },
    { id: "30d", label: "30d" },
];

const sourceFilters: Array<{ id: GlobalSearchType; label: string; icon: typeof Search }> = [
    { id: "all", label: "All", icon: Search },
    { id: "posts", label: "Posts", icon: MessageCircle },
    { id: "people", label: "People", icon: UserRound },
    { id: "spaces", label: "Spaces", icon: Orbit },
    { id: "notes", label: "Vault", icon: NotebookTabs },
    { id: "tags", label: "Tags", icon: Hash },
];

const sortModes: Array<{ id: GlobalSearchSort; label: string; icon: typeof Search }> = [
    { id: "relevance", label: "Best", icon: Sparkles },
    { id: "newest", label: "Newest", icon: ListFilter },
    { id: "popular", label: "Popular", icon: Sparkles },
];

const noteScopes: Array<{ id: GlobalSearchNoteScope; label: string }> = [
    { id: "accessible", label: "Vault access" },
    { id: "owned", label: "My notes" },
    { id: "shared", label: "Shared" },
];

const spaceScopes: Array<{ id: GlobalSearchSpaceAccess; label: string }> = [
    { id: "visible", label: "Visible spaces" },
    { id: "open", label: "Open only" },
    { id: "joined", label: "Joined" },
];

const introTiles: Array<{ id: GlobalSearchType; label: string; icon: typeof Search; className: string }> = [
    { id: "people", label: "People", icon: UserRound, className: "is-people" },
    { id: "posts", label: "Posts", icon: MessageCircle, className: "is-posts" },
    { id: "spaces", label: "Spaces", icon: Orbit, className: "is-spaces" },
    { id: "tags", label: "Tags", icon: Tag, className: "is-tags" },
    { id: "notes", label: "Vault", icon: LockKeyhole, className: "is-vault" },
];

function formatDate(value?: string) {
    return formatBlackspaceDateTime(value);
}

export default function GlobalSearch() {
    const isAnonymous = !localStorage.getItem("token");
    const navigate = useNavigate();
    const location = useLocation();
    const routeState = location.state as { from?: string; reopenBlackspaceMenuOnBack?: boolean } | null;
    const originRef = useRef<string | null>(routeState?.from || null);
    const reopenMenuOnBackRef = useRef(Boolean(routeState?.reopenBlackspaceMenuOnBack));
    const [params, setParams] = useSearchParams();
    const [query, setQuery] = useState(params.get("q") || "");
    const [submittedQuery, setSubmittedQuery] = useState(params.get("q") || "");
    const [searchOpen, setSearchOpen] = useState(Boolean(params.get("q")));
    const [filterOpen, setFilterOpen] = useState(false);
    const [contentType, setContentType] = useState<GlobalSearchType>((params.get("type") as GlobalSearchType) || "all");
    const [dateFilter, setDateFilter] = useState<GlobalSearchDate>((params.get("date") as GlobalSearchDate) || "all");
    const [sortMode, setSortMode] = useState<GlobalSearchSort>((params.get("sort") as GlobalSearchSort) || "relevance");
    const [noteScope, setNoteScope] = useState<GlobalSearchNoteScope>("accessible");
    const [spaceAccess, setSpaceAccess] = useState<GlobalSearchSpaceAccess>("visible");
    const [items, setItems] = useState<GlobalSearchItem[]>([]);
    const [totals, setTotals] = useState(emptyTotals);
    const [nextPage, setNextPage] = useState<number | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);

    const runSearch = useCallback(async (term: string, page = 1, signal?: AbortSignal) => {
        const clean = term.trim();
        setSubmittedQuery(clean);
        const nextParams: Record<string, string> = clean ? { q: clean } : {};
        if (contentType !== "all") nextParams.type = contentType;
        if (dateFilter !== "all") nextParams.date = dateFilter;
        if (sortMode !== "relevance") nextParams.sort = sortMode;
        setParams(nextParams);
        if (clean.length < 2) {
            setLoading(false);
            setLoadingMore(false);
            setError(null);
            setItems([]);
            setTotals(emptyTotals);
            setNextPage(null);
            setHasMore(false);
            return;
        }

        if (page === 1) setLoading(true);
        else setLoadingMore(true);
        setError(null);
        try {
            const result = await fetchGlobalSearch({
                q: clean,
                type: contentType,
                date: dateFilter,
                sort: sortMode,
                noteScope,
                spaceAccess,
                page,
                limit: SEARCH_LIMIT,
                signal,
            });
            setItems((current) => {
                if (page === 1) return result.items;
                const seen = new Set(current.map((item) => `${item.kind}:${item.id}`));
                return [...current, ...result.items.filter((item) => !seen.has(`${item.kind}:${item.id}`))];
            });
            setTotals(result.totals || emptyTotals);
            setNextPage(result.nextPage);
            setHasMore(Boolean(result.hasMore));
        } catch (err: any) {
            if (err?.name === "AbortError") return;
            setError(err?.message || "Search failed.");
            if (page === 1) {
                setItems([]);
                setTotals(emptyTotals);
                setNextPage(null);
                setHasMore(false);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [contentType, dateFilter, noteScope, setParams, sortMode, spaceAccess]);

    useEffect(() => {
        const initial = params.get("q") || "";
        setQuery(initial);
        if (initial.trim().length >= 2) setSearchOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!searchOpen) return;
        const timer = window.setTimeout(() => searchInputRef.current?.focus(), 60);
        return () => window.clearTimeout(timer);
    }, [searchOpen]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            runSearch(query, 1, controller.signal);
        }, query.trim().length >= 2 ? 320 : 180);
        return () => {
            window.clearTimeout(timer);
            abortRef.current?.abort();
        };
    }, [query, runSearch]);

    const loadMore = useCallback(() => {
        if (!hasMore || !nextPage || loading || loadingMore || submittedQuery.trim().length < 2) return;
        const controller = new AbortController();
        runSearch(submittedQuery, nextPage, controller.signal);
    }, [hasMore, loading, loadingMore, nextPage, runSearch, submittedQuery]);

    useEffect(() => {
        const node = loadMoreRef.current;
        if (!node) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) loadMore();
        }, { rootMargin: "280px 0px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [loadMore]);

    const grouped = useMemo(() => ({
        posts: items.filter((item) => item.kind === "post"),
        people: items.filter((item) => item.kind === "person"),
        spaces: items.filter((item) => item.kind === "space"),
        notes: items.filter((item) => item.kind === "note"),
        tags: items.filter((item) => item.kind === "tag"),
    }), [items]);

    const totalResults = totals.all || items.length;

    const hasSearch = submittedQuery.trim().length >= 2;
    const searchedLabel = submittedQuery.trim() ? `"${submittedQuery.trim()}"` : "Blackspace";
    const filterIsActive = contentType !== "all" || dateFilter !== "all" || sortMode !== "relevance" || noteScope !== "accessible" || spaceAccess !== "visible";
    const goBack = () => {
        const currentRoute = `${location.pathname}${location.search}${location.hash}`;
        if (originRef.current && originRef.current !== currentRoute) {
            navigate(originRef.current, { state: reopenMenuOnBackRef.current ? { reopenBlackspaceMenu: true } : undefined });
            return;
        }
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate("/home");
    };

    return (
        <div className="bs-page bs-global-search-page flex min-h-screen flex-col">
            <main className="bs-global-search-main mx-auto w-full max-w-6xl flex-1 px-4 py-5 pb-28 sm:px-8">
                <header className="bs-global-search-hero">
                    <div className="bs-global-search-orbit" aria-hidden="true" />
                    <div className="bs-global-search-topbar">
                        <button
                            type="button"
                            onClick={goBack}
                            className="bs-global-search-back"
                            title="Back"
                        >
                            <ArrowLeft size={14} />
                        </button>
                        <span className="bs-global-search-live bs-message-page-title">
                            <Sparkles size={14} />
                            Search
                        </span>
                        <div className="bs-global-search-actions">
                            <button
                                type="button"
                                className={`bs-home-mobile-command ${searchOpen || query.trim() ? "is-active" : ""}`}
                                onClick={() => {
                                    setSearchOpen((open) => !open);
                                    setFilterOpen(false);
                                }}
                                title="Search"
                                aria-label="Search"
                                aria-pressed={searchOpen}
                            >
                                <Search size={18} />
                            </button>
                            <button
                                type="button"
                                className={`bs-home-mobile-command ${filterOpen || filterIsActive ? "is-active" : ""}`}
                                onClick={() => {
                                    setFilterOpen((open) => !open);
                                    setSearchOpen(false);
                                }}
                                title="Filters"
                                aria-label="Filters"
                                aria-pressed={filterOpen}
                            >
                                <ListFilter size={18} />
                            </button>
                        </div>
                    </div>

                    {searchOpen && (
                        <div className="bs-home-mobile-sheet bs-global-search-feed-sheet">
                            <form
                                className="bs-home-mobile-search-form flex items-center gap-2"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    runSearch(query);
                                    searchInputRef.current?.blur();
                                }}
                            >
                                <Search size={17} className="shrink-0 text-cyan-200" />
                                <input
                                    ref={searchInputRef}
                                    type="search"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search posts, people, spaces..."
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    enterKeyHint="search"
                                    inputMode="search"
                                    className="bs-mobile-search-input min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setQuery("");
                                            setSubmittedQuery("");
                                            setItems([]);
                                            setTotals(emptyTotals);
                                            setNextPage(null);
                                            setHasMore(false);
                                            searchInputRef.current?.focus();
                                        }}
                                        className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-neutral-800 bg-black/45 text-neutral-400"
                                        aria-label="Clear search"
                                    >
                                        <X size={15} />
                                    </button>
                                )}
                            </form>
                        </div>
                    )}
                </header>

                {filterOpen && (
                    <section className="bs-home-mobile-sheet bs-global-search-feed-sheet bs-global-search-feed-filter" aria-label="Search filters">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {sourceFilters.filter(({ id }) => !isAnonymous || id !== "notes").map(({ id, label, icon: Icon }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setContentType(id)}
                                    className={`bs-home-mobile-filter-choice ${contentType === id ? "is-active" : ""}`}
                                >
                                    <Icon size={14} />
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {dateFilters.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setDateFilter(item.id)}
                                    className={`bs-home-mobile-filter-choice ${dateFilter === item.id ? "is-active" : ""}`}
                                >
                                    <CalendarDays size={14} />
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            {sortModes.map(({ id, label, icon: Icon }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setSortMode(id)}
                                    className={`bs-home-mobile-filter-choice ${sortMode === id ? "is-active" : ""}`}
                                >
                                    <Icon size={14} />
                                    {label}
                                </button>
                            ))}
                        </div>
                        {!isAnonymous && (contentType === "all" || contentType === "notes") && (
                            <div className="mt-2 grid grid-cols-3 gap-2">
                                {noteScopes.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => setNoteScope(item.id)}
                                        className={`bs-home-mobile-filter-choice ${noteScope === item.id ? "is-active" : ""}`}
                                    >
                                        <NotebookTabs size={14} />
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {(contentType === "all" || contentType === "spaces") && (
                            <div className="mt-2 grid grid-cols-3 gap-2">
                                {spaceScopes.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => setSpaceAccess(item.id)}
                                        className={`bs-home-mobile-filter-choice ${spaceAccess === item.id ? "is-active" : ""}`}
                                    >
                                        <Orbit size={14} />
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {error && <div className="bs-global-search-alert">{error}</div>}

                {loading && (
                    <div className="bs-global-search-skeleton-grid">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="bs-global-search-skeleton" />
                        ))}
                    </div>
                )}

                {!loading && !hasSearch && (
                    <SearchIntroCard
                        isAnonymous={isAnonymous}
                        title={searchOpen ? "Type at least two characters." : "Tap search to begin."}
                        subtitle={
                            searchOpen
                                ? `Search people, posts, spaces, and tags${isAnonymous ? "." : ", and your vault."}`
                                : `Search people, posts, spaces, and tags${isAnonymous ? "." : ", and your vault."}`
                        }
                        onOpenSearch={() => {
                            setSearchOpen(true);
                            setFilterOpen(false);
                        }}
                        onPickType={(nextType) => {
                            setContentType(nextType);
                            setSearchOpen(true);
                            setFilterOpen(false);
                        }}
                    />
                )}

                {!loading && hasSearch && totalResults === 0 && (
                    <div className="bs-global-search-empty">
                        <Sparkles size={36} />
                        <p>No signal found for {searchedLabel}.</p>
                        <span>Try a shorter phrase, a username, or a cleaner tag.</span>
                    </div>
                )}

                {!loading && totalResults > 0 && (
                    <div className="bs-global-search-results">
                        {grouped.posts.length > 0 && (
                            <ResultSection title="Posts" icon={MessageCircle} count={totals.posts || grouped.posts.length}>
                                <div className="bs-global-search-result-stack">
                                    {grouped.posts.map((post) => (
                                        <Link key={post.id} to={post.href || `/home#post-${post.id}`} className="bs-global-search-card bs-global-search-post">
                                            <div className="bs-global-search-card-icon"><MessageCircle size={16} /></div>
                                            <div className="min-w-0 flex-1">
                                                <div className="bs-global-search-card-meta">
                                                    <strong>{post.author?.displayName || post.author?.username || post.title || "Post"}</strong>
                                                    {(post.space?.name || post.subtitle) && <span>{post.space?.name || post.subtitle}</span>}
                                                    <small>{formatDate(post.createdAt || post.sortDate)}</small>
                                                </div>
                                                <p>{post.content}</p>
                                            </div>
                                            <ArrowUpRight size={15} className="bs-global-search-open" />
                                        </Link>
                                    ))}
                                </div>
                            </ResultSection>
                        )}

                        {grouped.people.length > 0 && (
                            <ResultSection title="People" icon={UserRound} count={totals.people || grouped.people.length}>
                                <div className="bs-global-search-grid">
                                    {grouped.people.map((user) => {
                                        const name = user.displayName || user.username || user.title;
                                        return (
                                            <Link key={user.id} to={user.href || `/user/${user.username}`} state={{ from: "/search", label: "Search" }} className="bs-global-search-card">
                                                {user.avatarUrl ? (
                                                    <img src={user.avatarUrl} alt="" className="bs-global-search-avatar" />
                                                ) : (
                                                    <span className="bs-global-search-avatar-fallback">{name.slice(0, 1).toUpperCase()}</span>
                                                )}
                                                <span className="min-w-0">
                                                    <span className="bs-global-search-name">{name}</span>
                                                    <span className="bs-global-search-sub">@{user.username}</span>
                                                </span>
                                                <ArrowUpRight size={15} className="bs-global-search-open" />
                                            </Link>
                                        );
                                    })}
                                </div>
                            </ResultSection>
                        )}

                        {grouped.spaces.length > 0 && (
                            <ResultSection title="Spaces" icon={Orbit} count={totals.spaces || grouped.spaces.length}>
                                <div className="bs-global-search-grid">
                                    {grouped.spaces.map((space) => (
                                        <Link
                                            key={space.id}
                                            to={space.href || `/spaces/${space.id}`}
                                            className="bs-global-search-card bs-global-search-space"
                                            style={{ "--search-accent": space.accentColor || "#00e5ff" } as CSSProperties}
                                        >
                                            <div className="bs-global-search-card-icon"><Orbit size={16} /></div>
                                            <div className="min-w-0 flex-1">
                                                <div className="bs-global-search-card-meta">
                                                    <strong>{space.title}</strong>
                                                    <span>{space.category || "General"}</span>
                                                </div>
                                                <p>{space.desc || "A Blackspace space."}</p>
                                                <small className="bs-global-search-sub">{space.members || 0} members</small>
                                            </div>
                                            <ArrowUpRight size={15} className="bs-global-search-open" />
                                        </Link>
                                    ))}
                                </div>
                            </ResultSection>
                        )}

                        {grouped.notes.length > 0 && (
                            <ResultSection title="Vault" icon={NotebookTabs} count={totals.notes || grouped.notes.length}>
                                <div className="bs-global-search-grid">
                                    {grouped.notes.map((note) => (
                                        <Link
                                            key={note.id}
                                            to={note.href || "/notes"}
                                            className="bs-global-search-card bs-global-search-note"
                                            style={{ "--search-accent": note.color || "#a855f7" } as CSSProperties}
                                        >
                                            <div className="bs-global-search-card-icon"><FileText size={16} /></div>
                                            <div className="min-w-0 flex-1">
                                                <div className="bs-global-search-card-meta">
                                                    <strong>{note.title || "Untitled note"}</strong>
                                                    <span>{note.type}</span>
                                                </div>
                                                <p>{note.content || "No body text."}</p>
                                                <small className="bs-global-search-sub">{note.wordCount || 0} words - {formatDate(note.updatedAt)}</small>
                                            </div>
                                            <ArrowUpRight size={15} className="bs-global-search-open" />
                                        </Link>
                                    ))}
                                </div>
                            </ResultSection>
                        )}

                        {grouped.tags.length > 0 && (
                            <ResultSection title="Tags" icon={Hash} count={totals.tags || grouped.tags.length}>
                                <div className="bs-global-search-tag-cloud">
                                    {grouped.tags.map((tag) => (
                                        <button
                                            key={`${tag.source}-${tag.tag || tag.id}`}
                                            type="button"
                                            onClick={() => {
                                                const nextTag = tag.tag || tag.title.replace(/^#/, "");
                                                setQuery(`#${nextTag}`);
                                            }}
                                            className="bs-global-search-tag"
                                        >
                                            {tag.title || `#${tag.tag}`}
                                            <span>{tag.count} - {tag.source}</span>
                                        </button>
                                    ))}
                                </div>
                            </ResultSection>
                        )}
                        <div ref={loadMoreRef} className="bs-global-search-load-sentinel" aria-hidden="true" />
                        {loadingMore && (
                            <div className="bs-global-search-loading-more">
                                <Sparkles size={15} className="animate-pulse" />
                                Loading more
                            </div>
                        )}
                    </div>
                )}
            </main>
            <FooterNav />
        </div>
    );
}

function ResultSection({ title, icon: Icon, count, children }: { title: string; icon: any; count: number; children: ReactNode }) {
    return (
        <section className="bs-global-search-section">
            <div className="bs-global-search-section-head">
                <h2>
                    <Icon size={16} />
                    {title}
                </h2>
                <span>
                    <UsersRound size={13} />
                    {count}
                </span>
            </div>
            {children}
        </section>
    );
}

function SearchIntroCard({
    title,
    subtitle,
    onOpenSearch,
    onPickType,
    isAnonymous,
}: {
    title: string;
    subtitle: string;
    onOpenSearch: () => void;
    onPickType: (type: GlobalSearchType) => void;
    isAnonymous: boolean;
}) {
    return (
        <section className="bs-global-search-intro-card" aria-label="Search start">
            <button type="button" className="bs-global-search-radar" onClick={onOpenSearch} aria-label="Open search">
                <span className="bs-global-search-radar-ring" aria-hidden="true" />
                <span className="bs-global-search-radar-particle is-one" aria-hidden="true" />
                <span className="bs-global-search-radar-particle is-two" aria-hidden="true" />
                <span className="bs-global-search-radar-particle is-three" aria-hidden="true" />
                <Search size={56} />
            </button>

            <div className="bs-global-search-intro-copy">
                <h1>{title}</h1>
                <p>{subtitle}</p>
            </div>

            <div className="bs-global-search-intro-tiles" aria-label="Search categories">
                {introTiles.filter(({ id }) => !isAnonymous || id !== "notes").map(({ id, label, icon: Icon, className }) => (
                    <button
                        key={id}
                        type="button"
                        className={`bs-global-search-intro-tile ${className}`}
                        onClick={() => onPickType(id)}
                    >
                        <Icon size={18} />
                        <span>{label}</span>
                    </button>
                ))}
            </div>

            <button type="button" className="bs-global-search-privacy-card">
                <span className="bs-global-search-privacy-icon">
                    <ShieldCheck size={21} />
                </span>
                <span className="bs-global-search-privacy-copy">
                    <strong>Private by design</strong>
                    <span>Your searches are private and never shared with anyone.</span>
                </span>
                <ChevronRight size={18} className="bs-global-search-privacy-arrow" />
            </button>
        </section>
    );
}
